import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, FolderTree, ArrowRight } from 'lucide-react';
import YouTubeMark from './YouTubeMark';

/**
 * The whole product in three steps. A first-time visitor (and, more to the
 * point, someone who has just signed up and is staring at a dashboard built
 * for sellers) needs one obvious next move, not a tour. Step one leads with
 * the YouTube badge because pasting a link is the cheapest possible first act.
 *
 *  - default  → a band under the hero on the landing page
 *  - compact  → a "Start here" card at the top of the dashboard
 */
const STEPS = [
  {
    n: '1',
    Mark: () => <YouTubeMark size={20} />,
    title: 'Paste a link.',
    body: 'A YouTube video, an article, a PDF — or just describe how you do the thing, in your own words.',
    short: 'Paste a YouTube link, an article, or a PDF.',
  },
  {
    n: '2',
    Mark: () => <Sparkles size={19} className="text-[var(--accent)]" />,
    title: 'Loreto writes the skill.',
    body: 'It reads the source and writes the package: SKILL.md, README, reference files, and a test you can actually run. Around two minutes.',
    short: 'Loreto writes SKILL.md, README, references and a test.',
  },
  {
    n: '3',
    Mark: () => <FolderTree size={19} className="text-[var(--accent)]" />,
    title: 'Your harness, one piece in.',
    body: 'Unzip it into .claude/skills/, or run the same skill as a CLI + MCP. Same model as yesterday — what changed is the harness around it. That is one piece; the rest comes from your own repository.',
    short: 'Unzip into .claude/skills/ — the first piece of the harness.',
  },
];

// Send the reader back up to the hero composer and put the caret in it.
function focusComposer() {
  const el = document.getElementById('loreto-composer');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  window.setTimeout(() => el.focus(), 420);
}

export function ThreeStepsCard() {
  return (
    <div className="border border-[var(--border)] rounded-sm bg-[var(--surface)] p-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="mono text-[0.72rem] tracking-[0.18em] uppercase text-[var(--text-3)]">
          Start here
        </div>
        <div className="mono text-[0.66rem] tracking-[0.14em] uppercase text-[var(--text-4)]">
          2 free skills a month
        </div>
      </div>
      <ol className="space-y-3.5 mb-6">
        {STEPS.map(({ n, Mark, short }) => (
          <li key={n} className="flex items-center gap-3 text-[0.92rem]">
            <span className="shrink-0 w-6 h-6 rounded-full border border-[var(--border-2)] flex items-center justify-center mono text-[0.7rem] text-[var(--text-3)]">
              {n}
            </span>
            <span className="shrink-0 w-5 flex items-center justify-center"><Mark /></span>
            <span className="text-[var(--text-2)] min-w-0">{short}</span>
          </li>
        ))}
      </ol>
      <Link
        to="/dashboard/generate"
        className="inline-flex items-center gap-2 rounded-full bg-[var(--text)] text-[var(--bg)] px-5 py-2.5 text-[0.9rem] font-medium hover:bg-[var(--accent)] transition-colors"
      >
        Build your first skill <ArrowRight size={15} />
      </Link>
    </div>
  );
}

export default function ThreeSteps() {
  return (
    <section
      className="relative border-t border-[var(--border)] bg-[var(--bg-2)]"
      aria-label="How Loreto works, in three steps"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="flex items-center gap-3 mb-10 text-[var(--text-3)]">
          <span className="eyebrow">Three steps to your first piece</span>
          <span className="flex-1 thin-line ml-2" />
          <button
            type="button"
            onClick={focusComposer}
            className="mono text-[0.68rem] tracking-[0.16em] uppercase text-[var(--text-3)] hover:text-[var(--accent)] transition-colors whitespace-nowrap"
          >
            Paste a link ↑
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
          {STEPS.map(({ n, Mark, title, body }) => (
            <div key={n} className="bg-[var(--bg-2)] p-7 md:p-9">
              <div className="flex items-center gap-3 mb-5">
                <span className="mono text-[0.72rem] tracking-[0.18em] text-[var(--text-4)]">
                  0{n}
                </span>
                <span className="flex items-center justify-center"><Mark /></span>
              </div>
              <h3 className="serif text-[1.5rem] md:text-[1.7rem] leading-tight mb-3">{title}</h3>
              <p className="text-[var(--text-3)] text-[0.94rem] leading-[1.65]">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 text-[var(--text-3)] mono text-[0.68rem] tracking-[0.16em] uppercase">
          Free to start — two skills a month, no card
        </div>
      </div>
    </section>
  );
}
